import { useState } from 'react';
import classNames from 'classnames';
import { DISCOUNT_TYPES } from 'constants/discount';
import ButtonFilters from './ButtonFilters';
import styles from './CategoryFilterList.module.scss';
import RangeFilters from './RangeFilters';
import TermsFilters from './TermsFilters';
import TreeFilters from './TreeFilters';

const LUME_BRAND_NAME_PRIORITY_SORT = 'Lume Cannabis Co.';
const OPTIONS_SHOWN_LIMIT = 10;
const EFFECTS_LIST = ['Dream', 'Recover', 'Unwind', 'Center', 'Focus', 'Move'];

const QUANTITY_LABELS = new Set([
  'Dosage',
  'Quantity_Total_Weight',
  'Quantity',
  'Size',
  'Weight',
]);

const SIZES = {
  '2XS': 1,
  XXS: 1,
  XS: 2,
  S: 4,
  M: 6,
  L: 8,
  XL: 10,
  XXL: 12,
  '2XL': 12,
  '3XL': 14,
  '4XL': 16,
  '5XL': 18,
  '6XL': 20,
  '7XL': 22,
  '8XL': 24,
};

function getAppropriateFilterByType(type) {
  switch (type) {
    case 'range': {
      return RangeFilters;
    }
    case 'tree': {
      return TreeFilters;
    }
    default: {
      return TermsFilters;
    }
  }
}

const sortTerms = ({ terms, filterParam, priorityTerms = [] }) => {
  const existingFilterQuery = decodeURIComponent(filterParam) || '';
  const appliedTerms = new Set(
    existingFilterQuery
      .trim()
      .split(',')
      .map((param) => param.split('^')[2])
  );

  const casedTerms = new Set(priorityTerms?.map((t) => t?.toLowerCase()) ?? []);

  return terms
    ? terms.sort((a, b) => {
        const aSelected = appliedTerms.has(a.term);
        const bSelected = appliedTerms.has(b.term);

        const aSortTerm = a.displayTerm || a.term;
        const bSortTerm = b.displayTerm || b.term;

        if (aSelected && !bSelected) {
          return -1;
        }

        if (!aSelected && bSelected) {
          return 1;
        }

        if (!aSelected && casedTerms.has(a.term?.toLowerCase())) {
          return -1;
        }

        return aSortTerm?.toLowerCase() < bSortTerm?.toLowerCase()
          ? -1
          : aSortTerm?.toLowerCase() > bSortTerm?.toLowerCase()
          ? 1
          : 0;
      })
    : terms;
};

const sortRanges = ({ ranges, filterParam }) => {
  const existingFilterQuery = decodeURIComponent(filterParam) || '';
  const appliedTerms = new Set(
    existingFilterQuery
      .trim()
      .split(',')
      .map((param) => param.split('^')[2])
  );
  return ranges
    ? ranges.sort((a, b) => {
        const aSelected = appliedTerms.has(
          a.term || `${a.from} to ${a.to || '*'}`
        );
        const bSelected = appliedTerms.has(
          b.term || `${b.from} to ${b.to || '*'}`
        );

        if (aSelected && !bSelected) {
          return -1;
        }

        if (bSelected && !aSelected) {
          return 1;
        }

        if (a.term && !b.term) {
          return 1;
        }

        if (!a.term && b.term) {
          return -1;
        }

        if (a.term && b.term) {
          return a.term < b.term ? -1 : 1;
        }

        return ranges.indexOf(a) - ranges.indexOf(b);
      })
    : ranges;
};

const sortTrees = ({ terms, filterParam }) => {
  const existingFilterQuery = decodeURIComponent(filterParam) || '';
  const appliedTerms = new Set(
    existingFilterQuery
      .trim()
      .split(',')
      .map((param) => param.split('^')[2])
  );
  return terms
    ? terms.sort((a, b) => {
        const aSelected = appliedTerms.has(a.id);
        const bSelected = appliedTerms.has(b.id);

        if (aSelected && !bSelected) {
          return -1;
        }

        if (bSelected && !aSelected) {
          return 1;
        }

        return terms.indexOf(a) - terms.indexOf(b);
      })
    : terms;
};

const sortEffects = ({ terms, filterParam }) => {
  const existingFilterQuery = decodeURIComponent(filterParam);
  return terms
    ? terms.sort((a, b) => {
        const aSelected = existingFilterQuery.includes(a.term);
        const bSelected = existingFilterQuery.includes(b.term);

        if (aSelected && !bSelected) {
          return -1;
        }

        if (bSelected && !aSelected) {
          return 1;
        }

        return EFFECTS_LIST.indexOf(a.term) - EFFECTS_LIST.indexOf(b.term);
      })
    : terms;
};

const WEIGHT_MAP = {
  '1/8oz': 3.5,
  '1/4oz': 7,
  '1/2oz': 14,
  '1oz': 28,
};

function sortVariants({ terms }) {
  /* eslint-disable complexity */
  return terms
    ? terms.sort((a, b) => {
        const labelA = a.displayTerm || a.term;
        const labelB = b.displayTerm || b.term;

        const parsedA =
          SIZES[labelA] || WEIGHT_MAP[labelA] || Number.parseFloat(labelA);
        const parsedB =
          SIZES[labelB] || WEIGHT_MAP[labelB] || Number.parseFloat(labelB);

        const finalA = Number.isNaN(parsedA)
          ? labelA && labelA.toLowerCase().includes('single')
            ? 0
            : Number.NaN
          : parsedA;
        const finalB = Number.isNaN(parsedB)
          ? labelB && labelB.toLowerCase().includes('single')
            ? 0
            : Number.NaN
          : parsedB;

        if (
          !Number.isNaN(finalA) &&
          !Number.isNaN(finalB) &&
          finalA !== finalB
        ) {
          return finalA - finalB;
        }

        if (!Number.isNaN(finalA) && Number.isNaN(finalB)) {
          return -1;
        }

        if (Number.isNaN(finalB) && !Number.isNaN(finalA)) {
          return 1;
        }

        if (labelA === labelB) {
          return 0;
        }

        return labelA > labelB ? 1 : -1;
      })
    : terms;
  /* eslint-enable complexity */
}

function applySort({
  facet,
  filterParam,
  filterType,
  isQuantityFilter,
  ranges,
  terms,
}) {
  if (filterType === 'range') {
    return sortRanges({ ranges, filterParam });
  }

  // Leave price filters in order they come in from getPlusMenu
  if (facet === 'Price') {
    return;
  }

  if (facet === 'Lume_Effect') {
    return sortEffects({ terms, filterParam });
  }

  if (isQuantityFilter) {
    return sortVariants({ terms });
  }

  if (facet === 'Brand') {
    return sortTerms({
      terms,
      filterParam,
      priorityTerms: [LUME_BRAND_NAME_PRIORITY_SORT],
    });
  }

  if (facet === 'Tier') {
    return;
  }

  if (filterType === 'term') {
    return sortTerms({ terms, filterParam });
  }

  return sortTrees({ terms, filterParam });
}

export default function CategoryFilterList({
  className,
  closeoutText,
  facet,
  filterParam,
  filterType,
  path,
  promotion,
  ranges,
  terms: propTerms,
}) {
  let terms = propTerms && propTerms.filter((val) => val.term);
  if (facet === 'Brand') {
    const parsedFilters = filterParam
      .split(',')
      .filter((val) => val.trim())
      .map((val) => decodeURIComponent(val).split('^'))
      // eslint-disable-next-line unicorn/no-array-reduce
      .reduce((acc, [key, _, value]) => {
        if (!acc[key]) acc[key] = [];
        acc[key].push({
          term: value,
          disallowMultiselect: false,
        });
        return acc;
      }, {});
    const termsMap = Object.fromEntries(terms.map((t) => [t.term, t]));
    const missingFilters =
      parsedFilters?.[facet]?.filter(
        (urlFilter) => !termsMap[urlFilter.term]
      ) ?? [];
    terms = [...missingFilters, ...terms];
  }

  if (facet === 'Special') {
    const filteredTerms = terms.filter(
      (term) => term.discountType !== DISCOUNT_TYPES.mixandmatch
    );
    terms = filteredTerms;
  }

  const [isListExpanded, setIsListExpanded] = useState(false);

  const handleOnClick = () => {
    setIsListExpanded(!isListExpanded);
  };

  const showExpansionButton =
    ranges?.length > OPTIONS_SHOWN_LIMIT || terms?.length > OPTIONS_SHOWN_LIMIT;

  const isQuantityFilter = QUANTITY_LABELS.has(facet);

  const FilterTag = isQuantityFilter
    ? ButtonFilters
    : getAppropriateFilterByType(filterType);

  applySort({
    facet,
    filterParam,
    filterType,
    isQuantityFilter,
    ranges,
    terms,
  });

  return (
    <>
      <ul
        className={classNames('list-unstyled', className)}
        data-value="filter-group"
      >
        <FilterTag
          closeoutText={closeoutText}
          facet={facet}
          filterParam={filterParam}
          isListExpanded={isListExpanded}
          limitToShow={OPTIONS_SHOWN_LIMIT}
          path={path}
          promotion={promotion}
          ranges={ranges}
          terms={terms}
        />
      </ul>
      {showExpansionButton && (
        <button
          className={classNames(
            'bg-transparent border-0 ps-0 text-primary',
            styles.expansion_button
          )}
          id={`${facet}-expand-button`}
          title={`Expand ${facet}`}
          type="button"
          onClick={handleOnClick}
        >
          {isListExpanded ? 'View Less' : 'View More'}
        </button>
      )}
    </>
  );
}
